import {
  activeJobsFor,
  findDuplicates,
  formatAddress,
  inspectorOptions,
  US_STATE_OPTIONS,
  type BidType,
  type Contact,
  type Job,
  type JobType,
} from "@roof-hawk/core";
import { useState } from "react";

import { AddressInput } from "../../components/AddressInput";
import {
  OptionalInspection,
  inspectionAssigneeColumns,
  inspectionIso,
  type InspectionDraft,
} from "../../components/InspectionFields";
import { Modal, PhoneInput } from "../../components/ui";
import { t } from "../../i18n";
import { pushCompanycamQueue } from "../../lib/companycam";
import { bookNewLeadInspection } from "../../lib/inspection";
import { insertRow } from "../../lib/queries";
import { useSession } from "../../session";

import { DuplicateWarning, OpenJobsWarning } from "./CustomerWarnings";
import { FindCustomer } from "./FindCustomer";

/**
 * New Lead / New Job.
 *
 * Two shapes in one modal: with a customer chosen it adds a job to them, and
 * without one it creates the customer and their first job. Duplicate detection
 * warns and offers to attach the job to the existing customer, but the rep can
 * always override — it never blocks (ADR 0007).
 *
 * A lead is always a job. It briefly was not: the modal asked whether there was
 * work to price yet, and a "no" wrote the customer on their own. Jonathon,
 * 2026-08-15, after Samuel created a lead and could not find it again: a
 * customer with no job is in no list, on no board and on nobody's dashboard,
 * because every one of those shows jobs. A lead nobody can see is worse than a
 * job with nothing in it yet — the empty job is the thing that gets worked.
 *
 * So the modal writes the job every time, hands it to whoever is signed in
 * unless they name somebody else, and lands them inside it.
 *
 * The inspection can be booked here too (Jonathon, 2026-09-28), with the
 * checklist's own form, and saving does what that box does.
 *
 * Adding a job to a customer who already has one still going — not closed,
 * not lost — asks "are you sure?" first and names the job they have
 * (Jonathon, 2026-10-06, ADR 0272). Like the duplicate check it asks and never
 * blocks: a customer can want a roof now and gutters next spring.
 */
export function NewLeadModal({
  contact,
  contacts,
  jobs,
  jobCountFor,
  onClose,
  onCreated,
}: {
  contact: Contact | null;
  contacts: readonly Contact[];
  /** Every job the rep can see, to find the customer's open ones. */
  jobs: readonly Job[];
  jobCountFor: (contactId: string) => number;
  onClose: () => void;
  /** The job that was just created — always a job, and always where we land. */
  onCreated: (jobId: string) => void | Promise<void>;
}) {
  const { profileId, leadSources, people, crews } = useSession();

  const [chosen, setChosen] = useState<Contact | null>(contact);
  const [duplicates, setDuplicates] = useState<Contact[] | null>(null);
  const [force, setForce] = useState(false);
  /** The customer's open jobs, once Create has been pressed and found some. */
  const [openJobs, setOpenJobs] = useState<Job[] | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneLabel, setPhoneLabel] = useState("");
  const [cell, setCell] = useState("");
  const [leadSource, setLeadSource] = useState(
    contact ? "Repeat Customer" : "",
  );
  const [address, setAddress] = useState(contact?.address ?? "");
  const [city, setCity] = useState(contact?.city ?? "");
  const [state, setState] = useState(contact?.state ?? "AL");
  const [zip, setZip] = useState(contact?.zip ?? "");
  const [coords, setCoords] = useState<{
    lat: number | null;
    lng: number | null;
  }>({ lat: contact?.lat ?? null, lng: contact?.lng ?? null });
  const [jobType, setJobType] = useState<JobType>("residential");
  const [bidType, setBidType] = useState<BidType>("repair");
  /** Whoever is creating it owns it, until they hand it to somebody else. */
  const [repId, setRepId] = useState(profileId ?? "");
  /** Null until somebody asks to book the inspection now. */
  const [inspection, setInspection] = useState<InspectionDraft | null>(null);
  const inspectors = inspectorOptions(people, crews);

  const isRepeat = Boolean(chosen);
  /*
   * Reps who are still here, and whoever is signed in.
   *
   * The signed-in person is in the list whether or not anybody has given them
   * the sales_rep role — they are creating the lead, so the job is theirs, and
   * a select whose value is missing from its options silently draws as the
   * first name in the list. That is how a lead ends up on somebody else's
   * board. Retired people are left out: a job can go on naming whoever sold
   * it, but a new one cannot be handed to somebody who has left.
   */
  const repOptions = people.filter(
    (person) =>
      person.id === profileId ||
      (person.active !== false && person.roles?.includes("sales_rep")),
  );
  /** What the contact will be called — the same rule the database applies. */
  const displayName =
    [firstName.trim(), lastName.trim()].filter(Boolean).join(" ") ||
    company.trim();

  function selectExistingCustomer(existing: Contact) {
    setChosen(existing);
    setDuplicates(null);
    setForce(false);
    setOpenJobs(null);
    setLeadSource("Repeat Customer");
    setAddress(existing.address ?? "");
    setCity(existing.city ?? "");
    setState(existing.state ?? "AL");
    setZip(existing.zip ?? "");
    setCoords({ lat: existing.lat, lng: existing.lng });
  }

  async function save(skipDuplicateCheck = false, confirmedAnother = false) {
    setError("");
    // Half a time is not an appointment; saving it as none loses the booking.
    if (inspection && Boolean(inspection.day) !== Boolean(inspection.slot)) {
      setError(
        t("Pick both a date and a time for the inspection, or remove it."),
      );
      return;
    }
    const inspectionAt = inspection ? inspectionIso(inspection) : null;
    setBusy(true);
    try {
      let contactId = chosen?.id;

      if (contactId && !confirmedAnother) {
        const open = activeJobsFor(jobs, contactId);
        if (open.length) {
          setOpenJobs(open);
          return;
        }
      }

      if (!contactId) {
        if (!displayName) {
          setError(
            t(
              "A last name, or a company name — one of them has to be filled in.",
            ),
          );
          return;
        }
        if (!skipDuplicateCheck && !force) {
          const hits = findDuplicates(contacts, {
            name: displayName,
            phone,
            cell,
            email,
          });
          if (hits.length) {
            setDuplicates(hits);
            return;
          }
        }
        const created = await insertRow<Contact>("contacts", {
          // full_name is set from these by a database trigger, and is sent as
          // well because the column is NOT NULL.
          full_name: displayName,
          first_name: firstName.trim() || null,
          last_name: lastName.trim() || null,
          company: company.trim() || null,
          email: email.trim() || null,
          phone: phone.trim() || null,
          phone_label: phoneLabel.trim() || null,
          cell: cell.trim() || null,
          address: address.trim() || null,
          city: city.trim() || null,
          state: state.trim() || null,
          zip: zip.trim() || null,
          lat: coords.lat,
          lng: coords.lng,
        });
        contactId = created.id;
      }

      const job = await insertRow<{ id: string }>("jobs", {
        contact_id: contactId,
        job_type: jobType,
        bid_type: bidType,
        lead_source: leadSource || null,
        address: address.trim() || null,
        city: city.trim() || null,
        state: state.trim() || null,
        zip: zip.trim() || null,
        lat: coords.lat,
        lng: coords.lng,
        created_by: profileId,
        rep_id: repId || profileId,
        ...(inspection && {
          ...inspectionAssigneeColumns(inspection),
          inspection_at: inspectionAt,
          inspection_notes: inspection.notes.trim() || null,
        }),
      });

      // The job exists from here, so the rep lands in it whatever else fails:
      // Create left enabled now means a second customer and job next press.
      if (inspection && inspectionAt) {
        await bookNewLeadInspection({
          jobId: job.id,
          minutes: inspection.durationMinutes,
          profileId,
        });
      }

      // The database has already queued this job's CompanyCam project
      // (migration 20260822100000). Ask the server to go and make it, rather
      // than leaving it for whoever opens the job next: the rep who just
      // created this lead is usually about to photograph the roof.
      //
      // Nothing waits for it and nothing shows if it fails — the row is still
      // in the queue either way, and the job page says so (ADR 0052).
      void pushCompanycamQueue();

      await onCreated(job.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={isRepeat ? t("New Job") : t("New Lead")}
      subtitle={
        isRepeat
          ? t(
              "Adds another job under this customer. Contact details stay shared across all of their jobs.",
            )
          : t(
              "Creates the customer and their first job. You're the sales rep unless you say otherwise.",
            )
      }
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            className="btn-primary"
            disabled={busy}
            onClick={() => void save()}
          >
            {isRepeat ? t("Create job") : t("Create lead")}
          </button>
        </>
      }
    >
      {isRepeat && chosen ? (
        <div className="picked">
          <div>
            <b>{chosen.full_name}</b>
            <div className="sub">
              {[formatAddress(chosen), chosen.cell ?? chosen.phone ?? ""]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
          <span className="chg" onClick={() => setChosen(null)}>
            {t("Change customer")}
          </span>
        </div>
      ) : (
        <FindCustomer
          contacts={contacts}
          jobCountFor={jobCountFor}
          onPick={selectExistingCustomer}
        />
      )}

      <div className="fgrid">
        {!isRepeat && (
          <>
            <div className="field">
              <label>{t("First name")}</label>
              <input
                value={firstName}
                autoComplete="given-name"
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="field">
              <label>{t("Last name")}</label>
              <input
                value={lastName}
                autoComplete="family-name"
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
            <div className="field full">
              <label>{t("Company")}</label>
              <input
                value={company}
                autoComplete="organization"
                placeholder={t("Only if the customer is a business")}
                onChange={(e) => setCompany(e.target.value)}
              />
            </div>
            <div className="field">
              <label>{t("Email")}</label>
              <input
                value={email}
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {/* The cell is asked for first because it is the number that gets
                answered, and the one every screen shows. The other number is
                as often a spouse's as a landline, so it is asked for as what
                it is and given a box to say whose it is. */}
            <div className="field">
              <label>{t("Cell")}</label>
              <PhoneInput value={cell} onChange={setCell} />
            </div>
            <div className="field">
              <label>{t("Alternate phone")}</label>
              <PhoneInput value={phone} onChange={setPhone} />
            </div>
            <div className="field full">
              <label>{t("Whose is the alternate?")}</label>
              <input
                value={phoneLabel}
                placeholder={t("Spouse, work, mother — anything that helps")}
                onChange={(e) => setPhoneLabel(e.target.value)}
              />
            </div>
          </>
        )}

        <div className={`field${isRepeat ? "" : " full"}`}>
          <label>{t("Lead source")}</label>
          <select
            value={leadSource}
            onChange={(e) => setLeadSource(e.target.value)}
          >
            <option value="">—</option>
            {leadSources.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </select>
        </div>

        <div className="field full">
          <label>{t("Job address (start typing)")}</label>
          <AddressInput
            value={address}
            onChange={setAddress}
            onPick={(pick) => {
              setAddress(pick.address);
              setCity(pick.city);
              if (pick.state) setState(pick.state);
              setZip(pick.zip);
              setCoords({ lat: pick.lat, lng: pick.lng });
            }}
          />
        </div>
        <div className="field">
          <label>{t("City")}</label>
          <input value={city} onChange={(e) => setCity(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="lead-state">{t("State")}</label>
          <select
            id="lead-state"
            value={state}
            onChange={(e) => setState(e.target.value)}
          >
            {US_STATE_OPTIONS.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>{t("Zip")}</label>
          <input value={zip} onChange={(e) => setZip(e.target.value)} />
        </div>
      </div>

      <div className="fgrid" style={{ marginTop: 10 }}>
        <div className="field">
          <label>{t("Job type")}</label>
          <select
            value={jobType}
            onChange={(e) => setJobType(e.target.value as JobType)}
          >
            <option value="residential">{t("Residential")}</option>
            <option value="commercial">{t("Commercial")}</option>
          </select>
        </div>
        <div className="field">
          <label>{t("Bid type")}</label>
          <select
            value={bidType}
            onChange={(e) => setBidType(e.target.value as BidType)}
          >
            <option value="repair">{t("Repair")}</option>
            <option value="retail">{t("Retail")}</option>
            <option value="insurance">{t("Insurance")}</option>
          </select>
        </div>
        <div className="field full">
          <label htmlFor="lead-rep">{t("Sales rep")}</label>
          <select
            id="lead-rep"
            value={repId}
            onChange={(e) => setRepId(e.target.value)}
          >
            {repOptions.map((person) => (
              <option key={person.id} value={person.id}>
                {person.full_name}
                {person.id === profileId ? ` ${t("(you)")}` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* At the bottom, as Jonathon asked: the customer, then the appointment. */}
      <OptionalInspection
        draft={inspection}
        options={inspectors}
        onChange={setInspection}
      />

      {duplicates && (
        <DuplicateWarning
          duplicates={duplicates}
          jobCountFor={jobCountFor}
          onUse={selectExistingCustomer}
          onDifferentPerson={() => {
            setForce(true);
            setDuplicates(null);
            void save(true);
          }}
        />
      )}

      {openJobs && chosen && (
        <OpenJobsWarning
          customerName={chosen.full_name}
          jobs={openJobs}
          onOpenJob={onClose}
          onConfirm={() => {
            setOpenJobs(null);
            void save(false, true);
          }}
        />
      )}

      {error && <div className="err">{error}</div>}
    </Modal>
  );
}
